import { Language } from '@cloud-native/workflows-engine'
import { JobStatusType } from 'features/jobs/types'
import { POLLING_INTERVAL } from 'features/jobs/state/jobConstants'
import { paramsToJobPayload } from '../../utils/mappers'
import { JobClientModel } from '../clients/JobsClient'
import { getStore } from 'app/state/storeRegistry'
import { getConnection } from 'features/connections/api/connections'
import { ProviderType } from 'features/connections'
import { calculateRegion } from 'features/workflows/utils/regionUtils'

interface Params {
  language: Language
  returnResults?: boolean
  signal?: AbortSignal
}

// TODO add support for signals

export class JobSqlQueryExecutor {
  #jobId: string | null = null

  #controller: AbortController | null = null

  #timeout = -1

  constructor(private readonly jobsClient: JobClientModel, private readonly params: Params) {
    window.onbeforeunload = () => {
      if (this.#timeout) {
        window.clearTimeout(this.#timeout)
      }
    }
  }

  async #poll() {
    if (!this.#jobId) {
      return Promise.reject()
    }

    if (this.#controller) {
      this.#controller.abort()
    }

    this.#controller = new AbortController()

    // TODO not using WorkflowStoreManager to avoid wrong coupling of classes
    // WorkflowManager (WorkflowStoreManager) <- ReduxManager (setters & getters)
    // JobSqlQueryExecutor <- ReduxManager (setters & getters)
    const workflow = getStore().getState().workflows.workflow

    const jobStatus = await this.jobsClient.getStatus(workflow.connectionName, this.#jobId, this.#controller.signal)

    if (jobStatus.status === JobStatusType.Pending || jobStatus.status === JobStatusType.Running) {
      return new Promise((resolve, reject) => {
        this.#timeout = window.setTimeout(async () => {
          try {
            const nextJobStatus = await this.#poll()

            resolve(nextJobStatus)
          } catch (e) {
            reject(e)
          }
        }, POLLING_INTERVAL)
      })
    }

    if (jobStatus.status === JobStatusType.Failure || jobStatus.status === JobStatusType.Cancelled) {
      return Promise.reject(jobStatus.error)
    }

    if (this.params?.returnResults) {
      if (typeof jobStatus?.provider?.results?.data === 'string') {
        return Promise.reject(jobStatus?.provider?.results?.data)
      }
    }

    return Promise.resolve(jobStatus?.provider?.results?.data)
  }

  async execute<T>(query: string) {
    // TODO not using WorkflowStoreManager to avoid wrong coupling of classes
    // WorkflowManager (WorkflowStoreManager) <- ReduxManager (setters & getters)
    // JobSqlQueryExecutor <- ReduxManager (setters & getters)
    const workflow = getStore().getState().workflows.workflow
    const tenantId = getStore().getState().users.me?.user_metadata.tenant_id || ''
    const connection = await getConnection(workflow.connectionId)
    const region = calculateRegion({
      tempLocation: workflow?.tempLocation,
      provider: connection?.provider_id as ProviderType,
      tenantId
    })

    const response = await this.jobsClient.create(
      workflow?.connectionName,
      paramsToJobPayload({
        region,
        query,
        workflowId: workflow.id,
        language: this.params.language,
        provider: workflow.config.connectionProvider
      })
    )

    this.#jobId = response.externalId

    return this.#poll() as Promise<T>
  }
}
